import type { Course } from "@/core/types";
import { Icon } from "@/ui/components/Icon";
import { StepTrack, type ProgressLookup } from "@/ui/components/Progress";
import { lessonStepsDone, type LessonRef } from "@/ui/progressStats";
import { href } from "./useHashRoute";

/** „Guten Morgen“, „Guten Tag“ oder „Guten Abend“ – nach der Uhrzeit. */
export function greeting(now: Date): string {
  const hour = now.getHours();
  if (hour >= 5 && hour < 11) return "Guten Morgen";
  if (hour >= 11 && hour < 18) return "Guten Tag";
  return "Guten Abend";
}

const dateFormat = new Intl.DateTimeFormat("de-DE", {
  weekday: "long",
  day: "numeric",
  month: "long",
});
const timeFormat = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" });
const dayFormat = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long" });

export function formatDate(date: Date): string {
  return dateFormat.format(date);
}

/** „heute, 14:32“, „gestern, 09:10“ oder „am 21. Oktober“. */
export function formatSaved(iso: string | undefined, now: Date): string | null {
  if (!iso) return null;
  const saved = new Date(iso);
  if (Number.isNaN(saved.getTime())) return null;
  const startOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(saved)) / 86_400_000);
  if (days === 0) return `heute, ${timeFormat.format(saved)}`;
  if (days === 1) return `gestern, ${timeFormat.format(saved)}`;
  return `am ${dayFormat.format(saved)}`;
}

interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav className="w-crumbs" aria-label="Brotkrumen">
      <ol>
        {items.map((item, index) => (
          <li key={item.label}>
            {item.to && index < items.length - 1 ? (
              <a href={item.to}>{item.label}</a>
            ) : (
              <span aria-current="page">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

interface NextLessonCardProps {
  course: Course;
  next: LessonRef | null;
  lookup: ProgressLookup;
  /** Name der Anwendung in der Kopfzeile zeigen (auf der Übersicht mit drei Kursen). */
  showCourse?: boolean;
  /** Die Lernziele der Lektion mit aufführen. */
  showGoals?: boolean;
}

/** Die nächste offene Lektion eines Kurses mit ihrem Schrittstand. */
export function NextLessonCard({
  course,
  next,
  lookup,
  showCourse = false,
  showGoals = false,
}: NextLessonCardProps) {
  if (!next) {
    return (
      <section className="w-panel w-next" data-host={course.host} aria-labelledby={`next-${course.id}`}>
        <h2 className="w-panel__title" id={`next-${course.id}`}>
          Als Nächstes
        </h2>
        <p className="w-next__title">Alle Lektionen sind abgeschlossen.</p>
        <p className="w-next__meta">Jede Lektion lässt sich weiter öffnen und nachlesen.</p>
      </section>
    );
  }

  const done = lessonStepsDone(next.module, next.lesson, lookup);
  const moduleNumber = course.modules.indexOf(next.module) + 1;

  return (
    <section className="w-panel w-next" data-host={course.host} aria-labelledby={`next-${course.id}`}>
      <header className="w-panel__head">
        <h2 className="w-panel__title" id={`next-${course.id}`}>
          Als Nächstes
        </h2>
        {showCourse ? (
          <span className="w-course-tag">
            <span className="w-dot" aria-hidden="true" />
            {course.host}
          </span>
        ) : null}
      </header>
      <p className="w-next__title">{next.lesson.title}</p>
      <p className="w-next__meta">
        Modul {moduleNumber} · {next.module.title}
      </p>
      <div className="w-next__track">
        <StepTrack
          steps={next.lesson.steps.map((step) => ({
            id: step.id,
            title: step.title,
            done: lookup.isStepDone(next.module.id, next.lesson.id, step.id),
          }))}
          current={-1}
        />
      </div>
      <p className="w-next__meta num">
        {done} von {next.lesson.steps.length} Schritten · {next.lesson.durationMinutes} Min.
      </p>
      {showGoals && next.lesson.learningGoals.length > 0 ? (
        <>
          <h3 className="w-next__label">Darin lernst du</h3>
          <ul className="w-next__goals">
            {next.lesson.learningGoals.slice(0, 3).map((goal) => (
              <li key={goal}>{goal}</li>
            ))}
          </ul>
        </>
      ) : null}
      <a
        className="btn btn--primary btn--block w-next__action"
        href={href.lesson(course.id, next.module.id, next.lesson.id)}
      >
        Lektion öffnen
        <Icon name="arrowRight" />
      </a>
    </section>
  );
}
